import { getContentIdFromPath } from '@databases/database-state';
import { DatabaseContentContextProvider } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import AppContent from '@databases/frontend/AppContent';
import AppWithContext from '@databases/frontend/AppWithContext';
import { perf } from '@databases/logs/performance';
import { appStore } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { setContext } from '@databases/utils/redux/context/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import React, { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import { dispatchInitialDatabaseLoad } from '../../bootstrap/dispatch-initial-database-load';

function getParentContentId() {
  try {
    // eslint-disable-next-line no-restricted-syntax
    const pathname = window.top && window.top.location.pathname;

    return pathname ? getContentIdFromPath(pathname) : null;
  } catch {
    // can't access window.top if cross-origin
    return null;
  }
}
type AppProps = {
  entryId: string | null;
  fieldId: string | null;
  referencing: boolean;
  savedViewId: string | null;
  unsavedViewId: string | null;
};

const App: React.FC<AppProps> = ({
  entryId,
  fieldId,
  referencing,
  savedViewId,
  unsavedViewId,
}) => {
  const {
    activationId,
    cloudId,
    contentId,
    contentReferenceId: referenceId,
    identity,
    spaceId,
    spaceKey,
    spaceName,
    title,
  } = useSnapshot(currentBridgeData).data;

  perf.timeLog('app-initialization', 'standalone-app-render');

  const previousContentId = useRef<string | null>(null);

  useEffect(() => {
    // Focus iframe to enable keyboard shortcuts
    window.focus();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(
        setContext({ space: { key: spaceKey }, userId: identity.accountId })
      );
    });
  }, [spaceKey, identity]);

  useEffect(() => {
    // Ensure that Confluence side panel gets closed when SPA transitioning between databases
    const handleConfluenceSidePanelClose = async () => {
      const bridge = await getBridgeAPI();

      if (bridge?.onCloseConfluenceSidePanel) {
        return bridge.onCloseConfluenceSidePanel();
      }
    };

    if (!previousContentId.current) {
      previousContentId.current = contentId;
    } else if (previousContentId.current !== contentId) {
      handleConfluenceSidePanelClose();
      previousContentId.current = contentId;
    }
  }, [contentId]);

  useEffect(() => {
    // In the referencing experience, `referenceId` and `spaceId` will never be
    // available while `contentId` only becomes available after a user selects a
    // database from the database dropdown. We could neither dispatch a load
    // even nor need to, so we skip it for the experience instead.
    if (referencing) {
      return;
    }

    if (referenceId) {
      dispatchInitialDatabaseLoad({
        contentId,
        contentReferenceId: referenceId,
        spaceId,
        spaceKey,
        spaceName,
        title,
      });
    }
  }, [contentId, referenceId, referencing, spaceId, spaceKey, spaceName, title]);

  return (
    <DatabaseContentContextProvider
      value={{
        activationId,
        cloudId,
        contentId,
        entryId,
        fieldId,
        parentContentId: getParentContentId(),
        referenceId,
        referencing,
        savedViewId,
        setContentId: () => {},
        spaceId,
        spaceKey,
        spaceName,
        title,
        unsavedViewId,
      }}
    >
      <AppWithContext aaid={identity.accountId}>
        <AppContent />
      </AppWithContext>
    </DatabaseContentContextProvider>
  );
};

export default React.memo(App);
